// /utils/courseExamScoring.js

function cellKey(row, column) {
  return `${row}§${column}`;
}

function questionWeightMultiplier(question) {
  const value = Number(question?.weight);
  if (!Number.isFinite(value) || value < 0) return 1;
  return value;
}

function isKnowledgeScoringEnabled(question) {
  return question?.knowledgeScoringEnabled !== false;
}

function maxPointsForQuestion(question) {
  if (!isKnowledgeScoringEnabled(question)) return 0;

  const type = String(question.type || "");

  if (type === "grid") {
    const cells = question.gridAnswers || [];
    return cells.reduce(
      (sum, cell) =>
        sum + (["correct", "tolerated"].includes(cell.validity) ? Number(cell.weight || 0) : 0),
      0
    );
  }

  if (type === "ranking") {
    return (question.options || []).reduce(
      (sum, option) => sum + Number(option.weight || 0),
      0
    );
  }

  if (type === "multiple") {
    return (question.options || []).reduce(
      (sum, option) =>
        sum + (["correct", "tolerated"].includes(option.validity) ? Number(option.weight || 0) : 0),
      0
    );
  }

  const correctOptions = (question.options || []).filter((option) =>
    ["correct", "tolerated"].includes(option.validity)
  );
  if (!correctOptions.length) return 1;
  return Math.max(...correctOptions.map((option) => Number(option.weight || 0)));
}

function scoreQuestion(question, answer = {}) {
  if (!isKnowledgeScoringEnabled(question)) {
    return { earnedPoints: 0, maxPoints: 0 };
  }

  const type = String(question.type || "");
  const multiplier = questionWeightMultiplier(question);
  const maxPoints = maxPointsForQuestion(question) * multiplier;
  let earnedPoints = 0;

  if (type === "grid") {
    const selections = answer.gridSelections || [];
    const selectedKeys = new Set(
      selections.map((cell) => cellKey(cell.row, cell.column))
    );

    for (const cell of question.gridAnswers || []) {
      const key = cellKey(cell.row, cell.column);
      if (selectedKeys.has(key)) {
        earnedPoints += Number(cell.weight || 0);
      }
    }

    return { earnedPoints: earnedPoints * multiplier, maxPoints };
  }

  if (type === "ranking") {
    const userOrder = answer.rankingOrder || [];
    const correctOrder = (question.options || []).map((option) => option.id);

    userOrder.forEach((optionId, index) => {
      if (correctOrder[index] === optionId) {
        const option = question.options.find((item) => item.id === optionId);
        earnedPoints += Number(option?.weight || 0);
      }
    });

    return { earnedPoints: earnedPoints * multiplier, maxPoints };
  }

  const selectedIds = new Set(answer.selectedOptionIds || []);

  if (type === "multiple") {
    for (const option of question.options || []) {
      if (selectedIds.has(option.id)) {
        earnedPoints += Number(option.weight || 0);
      }
    }
    return { earnedPoints: earnedPoints * multiplier, maxPoints };
  }

  const selected = (question.options || []).find((option) => selectedIds.has(option.id));
  if (selected) earnedPoints = Number(selected.weight || 0);

  return { earnedPoints: earnedPoints * multiplier, maxPoints };
}

export function flattenExamQuestions(modules = []) {
  const rows = [];
  for (const module of modules) {
    for (const question of module.questions || []) {
      rows.push({ module, question });
    }
  }
  return rows;
}

export function scoreCourseExam(modules = [], answersByQuestionId = {}) {
  const moduleResults = [];
  let earnedPoints = 0;
  let maxPoints = 0;
  const questionResults = [];

  for (const module of modules) {
    let moduleEarned = 0;
    let moduleMax = 0;

    for (const question of module.questions || []) {
      const answer = answersByQuestionId[question.questionId] || {};
      const result = scoreQuestion(question, answer);
      moduleEarned += result.earnedPoints;
      moduleMax += result.maxPoints;
      questionResults.push({
        moduleName: module.moduleName,
        question,
        answer,
        ...result,
      });
    }

    moduleResults.push({
      moduleId: module.moduleId,
      moduleName: module.moduleName,
      earnedPoints: moduleEarned,
      maxPoints: moduleMax,
    });

    earnedPoints += moduleEarned;
    maxPoints += moduleMax;
  }

  const percent = maxPoints > 0 ? Math.round((earnedPoints / maxPoints) * 100) : 0;

  return {
    earnedPoints,
    maxPoints,
    percent,
    moduleResults,
    questionResults,
  };
}

function shuffleArray(values) {
  const next = [...values];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function shuffledRankingOrder(question) {
  const correctOrder = (question.options || []).map((option) => option.id);
  if (correctOrder.length < 2) return [...correctOrder];

  let shuffled = shuffleArray(correctOrder);
  let attempts = 0;

  while (
    shuffled.every((optionId, index) => optionId === correctOrder[index]) &&
    attempts < 8
  ) {
    shuffled = shuffleArray(correctOrder);
    attempts += 1;
  }

  if (shuffled.every((optionId, index) => optionId === correctOrder[index])) {
    [shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]];
  }

  return shuffled;
}

export function createEmptyAnswer(question) {
  const type = String(question.type || "");

  if (type === "grid") {
    return { gridSelections: [] };
  }

  if (type === "ranking") {
    return {
      rankingOrder: shuffledRankingOrder(question),
    };
  }

  return { selectedOptionIds: [] };
}

export { cellKey };
